import { useEffect, useRef } from "react";
import { useMediaQuery } from "../lib/useMediaQuery";

/**
 * THE LIVING DUSK — a procedural canvas of warm light-motes that lives behind
 * the whole app, so the room itself feels quietly alive. Pure math, no assets,
 * no content: the no-runtime-generation invariant is untouched.
 *
 * Its one discipline is the brief's: the world reacts at story beats and goes
 * STILL during reading. While narration plays the motes nearly hold their
 * breath (slow, dim); at page boundaries / choices the room softly exhales
 * (breathe()). A child's finger leaves a faint trail of stardust that fades —
 * present, but barely noticed. At the story's end (setNight) the motes cool
 * into stars and a wish() can streak once across the sky.
 *
 * Calm + perf guardrails: ~44 motes + a capped trail, pre-rendered glow
 * sprites (no per-frame blur), DPR capped at 2, paused when the tab is hidden,
 * and the whole layer simply doesn't exist under prefers-reduced-motion.
 */

interface Burst {
  x: number;
  y: number;
  n: number;
}

interface WishStar {
  t0: number;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** Imperative bus: components nudge the world; the canvas reads it each frame. */
const ctl = {
  mounted: false,
  calm: false, // narration playing → the world holds its breath
  night: false,
  pulseAt: -1e9,
  bursts: [] as Burst[],
  wishPending: 0,
};

/** Reduced motion has no frame loop, so night changes notify a redraw instead. */
const nightListeners = new Set<() => void>();

export const stardust = {
  /** While true (narration playing) the motes still themselves. */
  setCalm(reading: boolean): void {
    ctl.calm = reading;
  },
  /** Story over: motes cool into stars and the room can hold a wish. */
  setNight(on: boolean): void {
    ctl.night = on;
    for (const fn of nightListeners) fn();
  },
  /** One soft exhale — fired at page boundaries and when a choice opens. */
  breathe(): void {
    ctl.pulseAt = performance.now();
  },
  /** A small bloom of motes at a viewport fraction (0..1, 0..1). */
  burst(fx: number, fy: number, n = 18): void {
    if (ctl.mounted) ctl.bursts.push({ x: fx, y: fy, n });
  },
  /** One shooting star. Returns false when the layer isn't running. */
  wish(): boolean {
    if (!ctl.mounted) return false;
    ctl.wishPending++;
    return true;
  },
};

interface Mote {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  phase: number;
  sway: number;
  swayHz: number;
  alpha: number;
  /** -1 for ambient motes; >=0 counts up to ttl for trail/burst motes. */
  life: number;
  ttl: number;
}

/** Pre-rendered radial glow so drawing a mote is one cheap drawImage. */
function glowSprite(rgb: string): HTMLCanvasElement {
  const s = 64;
  const c = document.createElement("canvas");
  c.width = c.height = s;
  const g = c.getContext("2d")!;
  const grad = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  grad.addColorStop(0, `rgba(${rgb},0.95)`);
  grad.addColorStop(0.35, `rgba(${rgb},0.4)`);
  grad.addColorStop(1, `rgba(${rgb},0)`);
  g.fillStyle = grad;
  g.fillRect(0, 0, s, s);
  return c;
}

export function Stardust() {
  const reduced = useMediaQuery("(prefers-reduced-motion: reduce)");
  const ref = useRef<HTMLCanvasElement | null>(null);

  // Reduced motion: the calm, STATIC collapse. The day page stays clean (the
  // dust's whole value is motion), but the night still gets its sky — a
  // deterministic scatter of soft stars drawn once, no loop, no twinkle, no
  // trails. Redrawn only when night toggles or the window resizes.
  useEffect(() => {
    if (!reduced) return;
    const canvas = ref.current;
    if (!canvas) return;
    const g = canvas.getContext("2d");
    if (!g) return;

    const frac = (i: number, seed: number) => {
      const v = Math.sin((i + 1) * seed) * 43758.5453;
      return v - Math.floor(v);
    };

    const draw = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const W = window.innerWidth;
      const H = window.innerHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      g.clearRect(0, 0, W, H);
      if (!ctl.night) return;
      for (let i = 0; i < 90; i++) {
        const x = frac(i, 12.9898) * W;
        const y = frac(i, 78.233) * H;
        const r = (0.6 + frac(i, 37.719) * 1.7) * 4;
        const a = 0.25 + frac(i, 94.673) * 0.5;
        const grad = g.createRadialGradient(x, y, 0, x, y, r);
        grad.addColorStop(0, `rgba(235,240,255,${a})`);
        grad.addColorStop(1, "rgba(235,240,255,0)");
        g.fillStyle = grad;
        g.beginPath();
        g.arc(x, y, r, 0, Math.PI * 2);
        g.fill();
      }
    };

    draw();
    nightListeners.add(draw);
    window.addEventListener("resize", draw);
    return () => {
      nightListeners.delete(draw);
      window.removeEventListener("resize", draw);
    };
  }, [reduced]);

  useEffect(() => {
    if (reduced) return;
    const canvas = ref.current;
    if (!canvas) return;
    const g = canvas.getContext("2d");
    if (!g) return;
    ctl.mounted = true;

    const warm = glowSprite("255,226,168"); // sunbeam dust
    const cool = glowSprite("216,226,255"); // starlight

    let W = 0;
    let H = 0;
    const resize = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const rnd = Math.random;
    const motes: Mote[] = [];
    for (let i = 0; i < 44; i++) {
      motes.push({
        x: rnd() * W,
        y: rnd() * H,
        vx: 0,
        vy: 5 + rnd() * 7, // px/s upward — slow as dust in lamplight
        r: 1.1 + rnd() * 1.6,
        phase: rnd() * Math.PI * 2,
        sway: 5 + rnd() * 12,
        swayHz: 0.12 + rnd() * 0.2,
        alpha: 0.28 + rnd() * 0.34,
        life: -1,
        ttl: -1,
      });
    }
    const trail: Mote[] = [];
    const wishes: WishStar[] = [];

    const addTrail = (x: number, y: number, n: number, spread: number) => {
      for (let k = 0; k < n; k++) {
        if (trail.length >= 70) trail.shift();
        const a = rnd() * Math.PI * 2;
        const sp = spread * (0.3 + rnd());
        trail.push({
          x: x + (rnd() - 0.5) * 6,
          y: y + (rnd() - 0.5) * 6,
          vx: Math.cos(a) * sp,
          vy: Math.sin(a) * sp - 14,
          r: 0.9 + rnd() * 1.3,
          phase: rnd() * 6,
          sway: 0,
          swayHz: 0,
          alpha: 0.5 + rnd() * 0.3,
          life: 0,
          ttl: 0.9 + rnd() * 0.6,
        });
      }
    };

    // A dragging finger (or held mouse) leaves stardust; a touch leaves a puff.
    let trailX = 0;
    let trailY = 0;
    let trailT = 0;
    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerType !== "touch" && e.buttons === 0) return;
      const t = performance.now();
      const dx = e.clientX - trailX;
      const dy = e.clientY - trailY;
      if (t - trailT < 26 || dx * dx + dy * dy < 160) return;
      trailT = t;
      trailX = e.clientX;
      trailY = e.clientY;
      addTrail(e.clientX, e.clientY, 2, 10);
    };
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType === "touch") addTrail(e.clientX, e.clientY, 5, 26);
    };
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("pointerdown", onPointerDown, { passive: true });

    let energy = 1;
    let nightMix = 0;
    let last = performance.now();
    let raf = 0;
    let running = true;

    const frame = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;

      // The world's breath: still while reading, an exhale on breathe().
      const base = ctl.calm ? 0.16 : 1;
      energy += (base - energy) * Math.min(1, dt * 1.4);
      const pulse = Math.exp(-(now - ctl.pulseAt) / 900) * 0.85;
      const eff = Math.min(1.6, energy + pulse);
      nightMix += ((ctl.night ? 1 : 0) - nightMix) * Math.min(1, dt * 0.7);

      while (ctl.bursts.length) {
        const b = ctl.bursts.shift()!;
        addTrail(b.x * W, b.y * H, b.n, 46);
      }
      while (ctl.wishPending > 0) {
        ctl.wishPending--;
        const x0 = W * (0.12 + rnd() * 0.4);
        const y0 = H * (0.06 + rnd() * 0.12);
        wishes.push({ t0: now, x0, y0, x1: x0 + W * 0.4, y1: y0 + H * 0.34 });
      }

      g.clearRect(0, 0, W, H);

      const drift = 0.22 + 0.78 * eff;
      for (const m of motes) {
        m.y -= m.vy * dt * drift * (1 - nightMix * 0.65);
        m.x += Math.sin((now / 1000) * m.swayHz * 2 * Math.PI + m.phase) * m.sway * dt;
        if (m.y < -24) {
          m.y = H + 18;
          m.x = rnd() * W;
        }
        if (m.x < -24) m.x = W + 20;
        else if (m.x > W + 24) m.x = -20;
        const tw = 0.62 + 0.38 * Math.sin((now / 1000) * 1.7 + m.phase * 3);
        const a = m.alpha * tw * (0.4 + 0.6 * eff) * (1 - 0.12 * nightMix);
        const size = m.r * 10;
        if (nightMix < 0.999) {
          g.globalAlpha = a * (1 - nightMix);
          g.drawImage(warm, m.x - size / 2, m.y - size / 2, size, size);
        }
        if (nightMix > 0.001) {
          g.globalAlpha = a * nightMix;
          g.drawImage(cool, m.x - size / 2, m.y - size / 2, size, size);
        }
      }

      for (let i = trail.length - 1; i >= 0; i--) {
        const m = trail[i];
        m.life += dt;
        if (m.life >= m.ttl) {
          trail.splice(i, 1);
          continue;
        }
        m.x += m.vx * dt;
        m.y += m.vy * dt;
        m.vy -= 10 * dt; // float gently upward as it fades
        m.vx *= 1 - 1.4 * dt;
        const k = 1 - m.life / m.ttl;
        const size = m.r * 9 * (0.6 + 0.4 * k);
        g.globalAlpha = m.alpha * k * (0.5 + 0.5 * eff);
        g.drawImage(nightMix > 0.5 ? cool : warm, m.x - size / 2, m.y - size / 2, size, size);
      }

      for (let i = wishes.length - 1; i >= 0; i--) {
        const w = wishes[i];
        const p = (now - w.t0) / 1200;
        if (p >= 1) {
          wishes.splice(i, 1);
          continue;
        }
        const hx = w.x0 + (w.x1 - w.x0) * p;
        const hy = w.y0 + (w.y1 - w.y0) * p;
        const tail = Math.min(p, 0.22);
        const tx = w.x0 + (w.x1 - w.x0) * (p - tail);
        const ty = w.y0 + (w.y1 - w.y0) * (p - tail);
        const fade = p < 0.7 ? 1 : 1 - (p - 0.7) / 0.3;
        const grad = g.createLinearGradient(tx, ty, hx, hy);
        grad.addColorStop(0, "rgba(255,240,200,0)");
        grad.addColorStop(1, `rgba(255,244,214,${0.85 * fade})`);
        g.globalAlpha = 1;
        g.strokeStyle = grad;
        g.lineWidth = 2.2;
        g.lineCap = "round";
        g.beginPath();
        g.moveTo(tx, ty);
        g.lineTo(hx, hy);
        g.stroke();
        const hs = 26 * fade;
        g.globalAlpha = 0.9 * fade;
        g.drawImage(warm, hx - hs / 2, hy - hs / 2, hs, hs);
      }

      g.globalAlpha = 1;
      raf = requestAnimationFrame(frame);
    };

    const onVis = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
      } else if (!running) {
        running = true;
        last = performance.now();
        raf = requestAnimationFrame(frame);
      }
    };
    document.addEventListener("visibilitychange", onVis);

    raf = requestAnimationFrame(frame);

    return () => {
      ctl.mounted = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [reduced]);

  return <canvas ref={ref} className="stardust" aria-hidden="true" />;
}
